import React, { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useOrganization } from '@/contexts/OrganizationContext'
import { billingService, type UsageTrendPoint } from '@/services/billingService'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DollarSign, Clock, CreditCard, AlertCircle, Download, ExternalLink } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { format, subDays } from 'date-fns'

import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'

const SERVICE_COLORS = {
  mdi_visit: '#00A79D',
  consulting_visit: '#633990',
  lab_order: '#3B82F6',
  at_home_phlebotomy: '#F59E0B',
} as const

const SERVICE_LABELS: Record<string, string> = {
  mdi_visit: 'MDI Visit',
  consulting_visit: 'Consulting',
  lab_order: 'Lab Order',
  at_home_phlebotomy: 'At-Home Phlebotomy',
}

const STATUS_BADGE: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  active: { variant: 'default', label: 'Active' },
  pending_setup: { variant: 'secondary', label: 'Pending Setup' },
  past_due: { variant: 'destructive', label: 'Past Due' },
  cancelled: { variant: 'outline', label: 'Cancelled' },
  trialing: { variant: 'secondary', label: 'Trial' },
  none: { variant: 'outline', label: 'Not Set Up' },
}

function getDateRange(preset: string): { start_date: string; end_date: string } {
  const end = format(new Date(), 'yyyy-MM-dd')
  const days = preset === 'last7' ? 7 : preset === 'last90' ? 90 : 30
  const start = format(subDays(new Date(), days), 'yyyy-MM-dd')
  return { start_date: start, end_date: end }
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-white p-3 border border-slate-200 rounded-lg shadow-lg">
        <p className="font-medium text-slate-900 mb-1">{label}</p>
        {payload.map((entry: any, i: number) => (
          <p key={i} className="text-sm" style={{ color: entry.color }}>
            {SERVICE_LABELS[entry.dataKey] || entry.dataKey}: <span className="font-semibold">{entry.value}</span>
          </p>
        ))}
      </div>
    )
  }
  return null
}

const OrgBilling: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>()
  const { currentOrg } = useOrganization()

  const { toast } = useToast()
  const [datePreset, setDatePreset] = useState('last30')

  const dateRange = useMemo(() => getDateRange(datePreset), [datePreset])

  const { data: overview, isLoading: isOverviewLoading } = useQuery({
    queryKey: ['org-billing-overview', orgId],
    queryFn: () => billingService.getOrgBillingOverview(orgId!),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  })

  const { data: trends, isLoading: isTrendsLoading } = useQuery({
    queryKey: ['org-billing-trends', orgId, dateRange],
    queryFn: () => billingService.getOrgUsageTrends(orgId!, { ...dateRange, granularity: datePreset === 'last7' ? 'day' : 'week' }),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  })

  const { data: revenue, isLoading: isRevenueLoading } = useQuery({
    queryKey: ['org-billing-revenue', orgId, dateRange],
    queryFn: () => billingService.getOrgBillingRevenue(orgId!, dateRange),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  })

  const { data: invoices, isLoading: isInvoicesLoading } = useQuery({
    queryKey: ['org-billing-invoices', orgId],
    queryFn: () => billingService.getOrgInvoices(orgId!),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  })

  const handleExportCSV = async () => {
    try {
      const blob = await billingService.exportOrgBillingCSV(orgId!)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `org-${orgId}-billing-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast({
        title: 'Export successful',
        description: 'Billing data exported to CSV',
        variant: 'success',
      })
    } catch (error: any) {
      toast({
        title: 'Export failed',
        description: error.message || 'Failed to export billing data',
        variant: 'destructive',
      })
    }
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)

  const chartData = useMemo(() => {
    if (!trends?.trends) return []
    return trends.trends.map((t: UsageTrendPoint) => ({
      ...t,
      formattedDate: format(new Date(t.period), 'MMM dd'),
    }))
  }, [trends])

  const revenueChartData = useMemo(() => {
    if (!revenue) return []
    const types = Object.keys(SERVICE_LABELS)
    return types.map(type => {
      const billed = revenue.billed?.[type]?.total || 0
      const unbilled = revenue.unbilled?.[type]?.total || 0
      return { name: SERVICE_LABELS[type], billed, unbilled, total: billed + unbilled }
    })
  }, [revenue])

  const kpiCards = [
    {
      title: 'Total Billed',
      value: overview ? formatCurrency(overview.totalBilled) : '$0.00',
      icon: DollarSign,
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      title: 'Total Unbilled',
      value: overview ? formatCurrency(overview.totalUnbilled) : '$0.00',
      icon: Clock,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
    {
      title: 'Active Subscriptions',
      value: overview?.activeSubscriptions ?? 0,
      icon: CreditCard,
      color: 'text-teal-600',
      bg: 'bg-teal-50',
    },
    {
      title: 'Needs Setup',
      value: overview?.clinicsNeedingSetup ?? 0,
      icon: AlertCircle,
      color: 'text-red-600',
      bg: 'bg-red-50',
    },
  ]

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Billing</h1>
          <p className="text-sm text-slate-500 mt-1">
            Track billing fees across {currentOrg?.name || 'organization'} clinics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleExportCSV} variant="outline" size="sm" className="gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
          <Select value={datePreset} onValueChange={setDatePreset}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last7">Last 7 days</SelectItem>
              <SelectItem value="last30">Last 30 days</SelectItem>
              <SelectItem value="last90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi) => (
          <Card key={kpi.title}>
            <CardContent className="pt-6">
              {isOverviewLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : (
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${kpi.bg}`}>
                    <kpi.icon className={`w-6 h-6 ${kpi.color}`} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{kpi.title}</p>
                    <p className="text-2xl font-bold text-slate-900">{kpi.value}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Usage Trends */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Usage Trends</CardTitle>
          </CardHeader>
          <CardContent>
            {isTrendsLoading ? (
              <Skeleton className="h-[280px] w-full" />
            ) : chartData.length === 0 ? (
              <div className="h-[280px] flex items-center justify-center text-slate-400">
                No usage data for this period
              </div>
            ) : (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 5, left: -15, bottom: 0 }}>
                    <defs>
                      {Object.entries(SERVICE_COLORS).map(([key, color]) => (
                        <linearGradient key={key} id={`color_${key}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis
                      dataKey="formattedDate"
                      axisLine={false}
                      tickLine={false}
                      fontSize={10}
                      fill="#64748b"
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      fontSize={10}
                      fill="#64748b"
                      width={40}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      formatter={(value: string) => SERVICE_LABELS[value] || value}
                      wrapperStyle={{ fontSize: '11px' }}
                    />
                    {Object.entries(SERVICE_COLORS).map(([key, color]) => (
                      <Area
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stackId="1"
                        stroke={color}
                        strokeWidth={2}
                        fill={`url(#color_${key})`}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue by Service */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Revenue by Service</CardTitle>
          </CardHeader>
          <CardContent>
            {isRevenueLoading ? (
              <Skeleton className="h-[280px] w-full" />
            ) : revenueChartData.length === 0 ? (
              <div className="h-[280px] flex items-center justify-center text-slate-400">
                No revenue data
              </div>
            ) : (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueChartData} layout="vertical" margin={{ top: 10, right: 5, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis
                      type="number"
                      axisLine={false}
                      tickLine={false}
                      fontSize={10}
                      fill="#64748b"
                      tickFormatter={(v: number) => `$${v.toFixed(0)}`}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      fontSize={10}
                      fill="#64748b"
                      width={90}
                    />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{ fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    />
                    <Bar dataKey="total" fill="#00A79D" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Clinic Billing Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Clinic Billing Status</CardTitle>
        </CardHeader>
        <CardContent>
          {isOverviewLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : !overview?.clinics?.length ? (
            <div className="py-12 text-center text-slate-400">
              No clinics have billing configured yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Clinic</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Status</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-500">Unbilled</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-500">Events</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Setup Date</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.clinics.map((clinic) => {
                    const badge = STATUS_BADGE[clinic.billingStatus] || STATUS_BADGE.none
                    return (
                      <tr 
                        key={clinic.id} 
                        className="border-b border-slate-100 hover:bg-slate-50"
                      >
                        <td className="py-3 px-4 font-medium text-slate-900">{clinic.name}</td>
                        <td className="py-3 px-4">
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-slate-700">
                          {formatCurrency(clinic.unbilledTotal)}
                        </td>
                        <td className="py-3 px-4 text-right text-slate-700">{clinic.eventCount}</td>
                        <td className="py-3 px-4 text-slate-500">
                          {clinic.setupDate ? format(new Date(clinic.setupDate), 'MMM dd, yyyy') : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Invoices</CardTitle>
        </CardHeader>
        <CardContent>
          {isInvoicesLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : !invoices?.length ? (
            <div className="py-12 text-center text-slate-400">
              No invoices found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Invoice ID</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Status</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-500">Amount</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-500">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice: any) => {
                    const statusBadge = STATUS_BADGE[invoice.status] || STATUS_BADGE.none
                    return (
                      <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 font-mono text-sm text-slate-700">{invoice.id}</td>
                        <td className="py-3 px-4 text-slate-700">
                          {format(new Date(invoice.created * 1000), 'MMM dd, yyyy')}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-slate-700">
                          {formatCurrency(invoice.amount_due / 100)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {invoice.invoice_pdf ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(invoice.invoice_pdf, '_blank')}
                              className="gap-2"
                            >
                              View
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          ) : (
                            <span className="text-slate-400 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default OrgBilling
